// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — StructureAdminPage
// Admin panel for Structure module — sidebar + section content
// ═══════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useI18n } from '../../../../../i18n';
import { useHasNivelModulo } from '../../../../../hooks/useModuloAcceso';
import { AdminPersonas } from '../components/AdminPersonas';
import { AdminPuestos } from '../components/AdminPuestos';
import { AdminDepartamentos } from '../components/AdminDepartamentos';
import { AdminOrganizaciones } from '../components/AdminOrganizaciones';
import { AdminCentros } from '../components/AdminCentros';
import { AdminNivelesPuesto } from '../components/AdminNivelesPuesto';
import { AdminRolesGobernanza } from '../components/AdminRolesGobernanza';
import { AdminCatalogos } from '../components/AdminCatalogos';
import { AdminUsuariosPersonas } from '../components/AdminUsuariosPersonas';
import { AdminTiposUnidad } from '../components/AdminTiposUnidad';
import { AdminRolesFuncionales } from '../components/AdminRolesFuncionales';
import { AdminGlosario } from '../components/AdminGlosario';
import { AdminOrganos } from '../components/AdminOrganos';
import { AdminTiposContrato } from '../components/AdminTiposContrato';
import { AdminCategoriasLaborales } from '../components/AdminCategoriasLaborales';
// 0642: workflow multi-etapa en su contexto del módulo M1.
import { AdminWorkflowsTab } from '../../../admin/pages/AdminWorkflowsTab';

type Section =
  | 'personas'
  | 'puestos'
  | 'departamentos'
  | 'tiposUnidad'
  | 'organizaciones'
  | 'centros'
  | 'niveles'
  | 'rolesFuncionales'
  | 'roles'
  | 'catalogos'
  | 'usuarios'
  | 'glosario'
  | 'organos'
  | 'tiposContrato'
  | 'categoriasLaborales'
  | 'workflow';

interface StructureAdminPageProps {
  embedded?: boolean;
}

// sidebarLabels[key] puede ser string (seccion vieja) u objeto con .label (seccion nueva, p.ej. tiposContrato)
function resolveLabel(value: unknown, fallback: string): string {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && 'label' in value) {
    const lbl = (value as { label?: unknown }).label;
    if (typeof lbl === 'string') return lbl;
  }
  return fallback;
}

// SVG icon paths per section (viewBox 0 0 24 24, strokeWidth 1.5)
const iconSvg = (paths: string) => (
  <svg
    width={16}
    height={16}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    dangerouslySetInnerHTML={{ __html: paths }}
  />
);

const SECTION_ICONS: Record<Section, React.ReactNode> = {
  personas: iconSvg(
    '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
  ),
  puestos: iconSvg(
    '<rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>',
  ),
  departamentos: iconSvg(
    '<path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>',
  ),
  organizaciones: iconSvg(
    '<rect x="2" y="3" width="20" height="18" rx="2"/><path d="M12 8v8"/><path d="M8 12h8"/>',
  ),
  centros: iconSvg(
    '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>',
  ),
  niveles: iconSvg('<path d="M22 12h-4l-3 9L9 3l-3 9H2"/>'),
  tiposUnidad: iconSvg(
    '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>',
  ),
  rolesFuncionales: iconSvg('<circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>'),
  roles: iconSvg(
    '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/>',
  ),
  catalogos: iconSvg(
    '<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>',
  ),
  usuarios: iconSvg(
    '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  ),
  glosario: iconSvg(
    '<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/><path d="M8 7h8M8 11h6"/>',
  ),
  organos: iconSvg(
    '<path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/><path d="M9 9h.01"/><path d="M9 13h.01"/><path d="M9 17h.01"/>',
  ),
  tiposContrato: iconSvg(
    '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/>',
  ),
  categoriasLaborales: iconSvg(
    '<path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><path d="M20 8v6"/><path d="M23 11h-6"/>',
  ),
  // 0642: workflow multi-etapa.
  workflow: iconSvg('<polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>'),
};

const SECTIONS: { key: Section; labelKey: string; fallback: string }[] = [
  { key: 'personas', labelKey: 'personas', fallback: 'Personas' },
  { key: 'puestos', labelKey: 'puestos', fallback: 'Puestos' },
  { key: 'niveles', labelKey: 'niveles', fallback: 'Niveles' },
  { key: 'rolesFuncionales', labelKey: 'rolesFuncionales', fallback: 'Roles Funcionales' },
  { key: 'roles', labelKey: 'roles', fallback: 'Roles gobernanza' },
  { key: 'departamentos', labelKey: 'departamentos', fallback: 'Departamentos' },
  { key: 'tiposUnidad', labelKey: 'tiposUnidad', fallback: 'Tipos de Unidad' },
  { key: 'organizaciones', labelKey: 'organizaciones', fallback: 'Organizaciones' },
  { key: 'centros', labelKey: 'centros', fallback: 'Centros' },
  { key: 'catalogos', labelKey: 'catalogos', fallback: 'Catalogos' },
  { key: 'usuarios', labelKey: 'usuarios', fallback: 'Usuarios ↔ Personas' },
  { key: 'glosario', labelKey: 'glosario', fallback: 'Glosario' },
  { key: 'organos', labelKey: 'organos', fallback: 'Órganos de Gobierno' },
  { key: 'tiposContrato', labelKey: 'tiposContrato', fallback: 'Tipos de Contrato' },
  {
    key: 'categoriasLaborales',
    labelKey: 'categoriasLaborales',
    fallback: 'Categorias Laborales',
  },
  // 0642: workflow multi-etapa filtrado a ORGANIGRAMA (modulo M1 Structure).
  {
    key: 'workflow',
    labelKey: 'workflow',
    fallback: 'Workflow',
  },
];

export function StructureAdminPage({ embedded }: StructureAdminPageProps) {
  const { t } = useI18n();
  const [section, setSection] = useState<Section>('personas');
  const adm = (t.structure as Record<string, unknown>)?.admin as
    | Record<string, unknown>
    | undefined;
  // El contenedor structure.admin tiene las labels de cada seccion al nivel raiz
  // (personas, puestos, glosario...), sin sub-objeto sidebar.
  const sidebarLabels = (adm ?? {}) as Record<string, string>;
  // 0615e fix: gate por nivel del modulo M1 (ADR-024) en lugar de rol RBAC base.
  // El check anterior `isAdmin` solo aceptaba SUPERADMIN/OWNER/TENANT_ADMIN, lo
  // cual era inconsistente con el gate del tab en StructurePage.tsx que ya usa
  // `useHasNivelModulo('M1', 'ADMIN')`. Asi un MEMBER con ADMIN M1 podia ver
  // el tab pero le rechazaba el contenido. Ahora ambos puntos coinciden.
  // Cortocircuita por rol: SUPERADMIN/OWNER/TENANT_ADMIN -> ADMIN implicito.
  const canAdminM1 = useHasNivelModulo('M1', 'ADMIN');

  if (!canAdminM1) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: embedded ? '100%' : 'calc(100vh - 60px)',
          color: '#64748B',
          fontSize: 14,
        }}
      >
        {(t.structure as any)?.admin?.noPermission ??
          'No tienes permisos para acceder a esta seccion'}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        height: embedded ? '100%' : 'calc(100vh - 60px)',
        background: '#fff',
        borderRadius: embedded ? 0 : 12,
        overflow: 'hidden',
      }}
    >
      {/* Sidebar */}
      <div
        style={{
          width: 200,
          minWidth: 200,
          background: '#F8F9FC',
          borderRight: '1px solid #E5E7EB',
          display: 'flex',
          flexDirection: 'column',
          padding: 8,
          gap: 4,
        }}
      >
        <div
          style={{
            padding: '6px 8px 6px',
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: '#94A3B8',
          }}
        >
          {(adm?.title as string) ?? 'Administracion'}
        </div>
        {SECTIONS.map((s) => {
          const active = section === s.key;
          return (
            <button
              key={s.key}
              onClick={() => setSection(s.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                padding: '9px 12px',
                borderRadius: 8,
                border: active ? 'none' : '1px solid #E5E7EB',
                background: active ? '#1F3864' : 'transparent',
                color: active ? '#fff' : '#64748B',
                fontSize: 13,
                fontWeight: active ? 600 : 400,
                cursor: 'pointer',
                transition: 'all 0.15s',
                fontFamily: 'inherit',
                boxShadow: active ? 'inset 3px 0 0 #33BCE1' : 'none',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => {
                if (!active) {
                  e.currentTarget.style.background = '#F0F4FA';
                  e.currentTarget.style.color = '#1F3864';
                  e.currentTarget.style.border = '1px solid #D1D5DB';
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#64748B';
                  e.currentTarget.style.border = '1px solid #E5E7EB';
                }
              }}
            >
              {SECTION_ICONS[s.key]}
              {resolveLabel(sidebarLabels[s.labelKey], s.fallback)}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0, overflow: 'auto', padding: 20 }}>
        {section === 'personas' && <AdminPersonas />}
        {section === 'puestos' && <AdminPuestos />}
        {section === 'departamentos' && <AdminDepartamentos />}
        {section === 'tiposUnidad' && <AdminTiposUnidad />}
        {section === 'organizaciones' && <AdminOrganizaciones />}
        {section === 'centros' && <AdminCentros />}
        {section === 'niveles' && <AdminNivelesPuesto />}
        {section === 'rolesFuncionales' && <AdminRolesFuncionales />}
        {section === 'roles' && <AdminRolesGobernanza />}
        {section === 'catalogos' && <AdminCatalogos />}
        {section === 'usuarios' && <AdminUsuariosPersonas />}
        {section === 'glosario' && <AdminGlosario />}
        {section === 'organos' && <AdminOrganos />}
        {section === 'tiposContrato' && <AdminTiposContrato />}
        {section === 'categoriasLaborales' && <AdminCategoriasLaborales />}
        {section === 'workflow' && <AdminWorkflowsTab artefactoFijo="ORGANIGRAMA" />}
      </div>
    </div>
  );
}
